import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { ansConsistencyParams, fetchAnsConsistency } from '@/services/ans';

const PAGE_SIZE = 50;

/**
 * ANS consistency review: board-reviewed cases alike in track and object whose
 * sourced final fines diverged.
 *
 * The pairs exist on the tracks that carry an object; the consumer track --
 * most of the corpus -- has none and is refused on its face, with the case
 * page's tabulated-amount group named as its comparator. A divergent pair is
 * a potential inconsistency, never a finding, and the queue is ordered by
 * object and case number, never by the size of the gap.
 */
export function AnsConsistencyPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const [seed] = useSearchParams();
  const [object, setObject] = useState(seed.get('object') ?? '');
  const filters = { object: object || null, track: null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ans', 'consistency', filters],
    queryFn: ({ pageParam }) =>
      fetchAnsConsistency({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const trajectory = (code: string) => t(`ans:cases.trajectories.${code}`, { defaultValue: code });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:consistency.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/ans/export/consistency?${ansConsistencyParams(filters).toString()}`}
          label={t('ans:consistency.export')}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-lg border bg-card p-4" data-funnel>
              <h2 className="mb-3 text-sm font-semibold text-foreground">
                {t('ans:consistency.funnel.title')}
              </h2>
              <Answer answer={data.funnel} help={t('ans:consistency.funnel.help')}>
                {(f) => (
                  <div className="grid grid-cols-2 gap-3">
                    <Figure
                      label={t('ans:consistency.funnel.pairs')}
                      value={number.format(f.pairs)}
                    />
                    <Figure
                      label={t('ans:consistency.funnel.bothReviewed')}
                      value={number.format(f.pairs_both_reviewed)}
                    />
                    <Figure
                      label={t('ans:consistency.funnel.divergent')}
                      value={`${number.format(f.pairs_divergent)} (${percent.format(
                        f.divergent_share_of_reviewed_pairs,
                      )})`}
                    />
                    <Figure
                      label={t('ans:consistency.funnel.cases')}
                      value={`${number.format(f.cases_in_divergent_pairs)} / ${number.format(
                        f.cases_with_pair,
                      )}`}
                    />
                  </div>
                )}
              </Answer>
              <div className="mt-3">
                <FieldGuide i18nKey="ans:consistency.guide" />
              </div>
            </section>

            <RefusalCard
              title={t('ans:consistency.consumerTrack.title')}
              answer={data.consumer_track}
            />
          </div>

          <section className="rounded-lg border bg-card p-4" data-by-object>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('ans:consistency.objects.title')}
            </h2>
            <Answer answer={data.by_object} help={t('ans:consistency.objects.help')}>
              {(rows) => (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">
                          {t('ans:consistency.objects.object')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('ans:consistency.objects.pairs')}
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          {t('ans:consistency.objects.divergent')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.object} className="border-t" data-object={row.object}>
                          <td className="px-3 py-2 text-foreground">
                            {row.divergent > 0 ? (
                              <button
                                type="button"
                                onClick={() => setObject(row.object)}
                                className="text-left text-primary underline-offset-2 hover:underline"
                              >
                                {row.object}
                              </button>
                            ) : (
                              row.object
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {number.format(row.pairs)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-foreground">
                            {number.format(row.divergent)}
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({percent.format(row.divergent_share)})
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Answer>
          </section>
        </>
      ) : null}

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
        <div className="space-y-1">
          <label htmlFor="ans-pair-object" className="block text-xs font-medium text-foreground">
            {t('ans:consistency.queue.filterObject')}
          </label>
          <select
            id="ans-pair-object"
            value={object}
            onChange={(event) => setObject(event.target.value)}
            className="max-w-[24rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('ans:consistency.queue.anyObject')}</option>
            {(data?.objects ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.value} ({number.format(option.cases)})
              </option>
            ))}
          </select>
        </div>
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ans:consistency.queue.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="px-4 py-2 text-xs text-muted-foreground" data-total={data?.total}>
            {t('ans:consistency.queue.count', {
              loaded: number.format(items.length),
              total: number.format(data?.total ?? 0),
            })}
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ans:consistency.queue.object')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:consistency.queue.caseA')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:consistency.queue.caseB')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((pair) => (
                  <tr
                    key={`${pair.case_a}-${pair.case_b}`}
                    className="border-t align-top"
                    data-pair={`${pair.case_a}-${pair.case_b}`}
                  >
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {pair.object}
                      <div>{pair.track}</div>
                    </td>
                    {(
                      [
                        [
                          pair.case_a,
                          pair.entity_a,
                          pair.year_a,
                          pair.trajectory_a,
                          pair.original_a,
                          pair.final_a,
                          pair.outcome_a,
                        ],
                        [
                          pair.case_b,
                          pair.entity_b,
                          pair.year_b,
                          pair.trajectory_b,
                          pair.original_b,
                          pair.final_b,
                          pair.outcome_b,
                        ],
                      ] as const
                    ).map(([key, entity, year, traj, original, final, outcome]) => (
                      <td key={key} className="px-3 py-2">
                        <Link
                          to={`/ans/c/${key}`}
                          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
                        >
                          {key}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {entity ?? '—'}
                          {year ? ` · ${year}` : ''}
                        </div>
                        <div className="text-xs text-foreground">
                          {trajectory(traj)}: {original === null ? '—' : currency.format(original)}{' '}
                          → {final === null ? '—' : currency.format(final)}
                        </div>
                        {outcome ? (
                          <div className="text-xs text-muted-foreground">{outcome}</div>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage
                  ? t('common:state.loading')
                  : t('ans:consistency.queue.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
